import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@ui';
import { AlertCircle, Loader2, Trash2, Power, PowerOff, Info, Lightbulb } from 'lucide-react';
import type { PricingPlan } from '../types';

interface ToggleConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    plan: PricingPlan | null;
    onConfirm: () => void;
    isPending: boolean;
}

export function ToggleConfirmDialog({ isOpen, onClose, plan, onConfirm, isPending }: ToggleConfirmDialogProps) {
    if (!plan) return null;

    const isActivating = !plan.active;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isActivating ? (
                            <Power className="h-5 w-5 text-green-600" />
                        ) : (
                            <PowerOff className="h-5 w-5 text-amber-600" />
                        )}
                        이용권 {isActivating ? '활성화' : '비활성화'}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <p className="text-neutral-700 dark:text-neutral-200">
                        <span className="font-semibold">"{plan.name}"</span> 이용권을{' '}
                        {isActivating ? '활성화' : '비활성화'}하시겠습니까?
                    </p>

                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                        <div className="text-sm text-blue-700 space-y-1">
                            {isActivating ? (
                                <>
                                    <p>활성화 시:</p>
                                    <ul className="list-disc list-inside text-xs space-y-0.5">
                                        <li>신규 보호자에게 이 이용권을 할당할 수 있습니다</li>
                                        <li>이용권 목록에서 기본으로 표시됩니다</li>
                                    </ul>
                                </>
                            ) : (
                                <>
                                    <p>비활성화 시:</p>
                                    <ul className="list-disc list-inside text-xs space-y-0.5">
                                        <li>신규 보호자에게 이 이용권을 할당할 수 없습니다</li>
                                        <li>이미 구독 중인 보호자의 잔여 횟수/금액에는 영향이 없습니다</li>
                                        <li>언제든지 다시 활성화할 수 있습니다</li>
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} disabled={isPending}>
                        취소
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isPending}
                        className={isActivating ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
                    >
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                처리 중...
                            </>
                        ) : (
                            isActivating ? '활성화' : '비활성화'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface DeleteConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    plan: PricingPlan | null;
    onConfirm: () => void;
    isPending: boolean;
}

export function DeleteConfirmDialog({ isOpen, onClose, plan, onConfirm, isPending }: DeleteConfirmDialogProps) {
    if (!plan) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-danger-600">
                        <AlertCircle className="h-5 w-5" />
                        이용권 삭제
                    </DialogTitle>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <p className="text-neutral-700 dark:text-neutral-200">
                        <span className="font-semibold text-danger-600">"{plan.name}"</span> 이용권을
                        삭제하시겠습니까?
                    </p>

                    <div className="flex items-start gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-danger-600 mt-0.5 shrink-0" />
                        <div className="text-sm text-danger-700 space-y-1">
                            <p>삭제 시:</p>
                            <ul className="list-disc list-inside text-xs space-y-0.5">
                                <li>이용권 목록에서 더 이상 표시되지 않습니다</li>
                                <li>신규 보호자에게 이 이용권을 할당할 수 없습니다</li>
                                <li>이미 구독 중인 보호자의 잔여 횟수/금액에는 영향이 없습니다</li>
                                <li className="font-medium">삭제된 이용권은 복구할 수 없습니다</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-sm text-amber-700">
                            일시적으로 판매를 중단하려면 '비활성화'를 사용하세요
                        </p>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} disabled={isPending}>
                        취소
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isPending}
                        className="bg-danger-500 hover:bg-danger-600"
                    >
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                삭제 중...
                            </>
                        ) : (
                            <>
                                <Trash2 className="mr-2 h-4 w-4" />
                                삭제
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
